import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/constants/brand";

export default function AdminLogin() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back!");
            navigate("/admin", { replace: true });
        } catch (err) {
            toast.error(
                formatApiError(err?.response?.data?.detail) || err.message,
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid="admin-login"
            className="min-h-screen bg-forest-900 flex items-center justify-center p-5 relative overflow-hidden"
        >
            <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-gold-500/10 blur-3xl" />
            <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-forest-700/60 blur-3xl" />
            <div className="relative z-10 w-full max-w-md">
                <Link
                    to="/"
                    className="flex items-center gap-3 justify-center mb-8"
                >
                    <img
                        src={LOGO_URL}
                        alt="Resham Drops"
                        className="w-14 h-14 rounded-full border-2 border-gold-500 object-cover"
                    />
                    <div className="text-cream">
                        <div className="font-serif font-bold text-xl">
                            Resham Drops
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-gold-400">
                            Admin Console
                        </div>
                    </div>
                </Link>

                <div className="bg-cream rounded-3xl p-7 sm:p-9 shadow-2xl border border-gold-500/20">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-forest-800 text-gold-400 flex items-center justify-center">
                            <Lock className="w-5 h-5" />
                        </div>
                        <div>
                            <h1 className="font-serif text-xl font-bold text-forest-800">
                                Sign in to continue
                            </h1>
                            <p className="text-xs text-forest-900/60">
                                Only authorised Resham Drops team members.
                            </p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-[11px] font-bold text-forest-800 uppercase tracking-widest mb-1.5">
                                Email
                            </label>
                            <input
                                data-testid="login-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@reshamdrops.com"
                                className="w-full bg-white border border-forest-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500 transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-forest-800 uppercase tracking-widest mb-1.5">
                                Password
                            </label>
                            <input
                                data-testid="login-password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="w-full bg-white border border-forest-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500 transition-all"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="login-submit"
                            disabled={loading}
                            className="w-full bg-forest-800 hover:bg-forest-900 disabled:opacity-70 text-gold-400 font-bold text-sm py-3.5 rounded-xl shadow transition-all flex items-center justify-center gap-2 border border-gold-500/40"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <LogIn className="w-4 h-4" />
                            )}
                            Sign In
                        </button>
                    </form>
                </div>
                <p className="text-center text-[11px] text-cream/60 mt-5">
                    Not an admin?{" "}
                    <Link to="/" className="text-gold-400 hover:underline">
                        Back to store
                    </Link>
                </p>
            </div>
        </div>
    );
}
